import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { PlainButton, Icon, Loading, Flex } from "@evertrue/et-components";
import { useChatNavigationContext } from "../contexts/chat-navigation-context";
import ChatEtUserCard from "./chat-et-user-card";
import { getUserTotalFromRoom } from "../utils/utils";
import { useChatDependencyContext } from "../contexts/chat-dependency-context";

// split into 2 components here to make styling easier
export const ChatUserLink = ({ onClick, userId, children, room = {}, className }) => {
  const [, dispatchNav] = useChatNavigationContext();
  const { createDM, mixPanelEvents } = useChatDependencyContext();

  if (!room.id && !userId) {
    // would be an 'unknown user'
    return (
      <div style={{ padding: 20, background: "#fff" }} className={className}>
        <ChatEtUserCard avatarClassName="chat-room-link--left-column" userId={userId}>
          {children && <div className="chat-room-link--message-snippet">{children}</div>}
        </ChatEtUserCard>
      </div>
    );
  }
  return (
    <PlainButton
      className={`chat-room-link ${className ? className : ""}`}
      onClick={() => {
        mixPanelEvents.startDM();
        if (room.id) {
          dispatchNav({ type: "SELECT_ROOM", payload: room.id });
        } else {
          createDM({ user_id: userId }).then(r => {
            dispatchNav({ type: "SELECT_ROOM", payload: r.external_room_id });
          });
        }
        onClick();
      }}
    >
      <ChatEtUserCard avatarClassName="chat-room-link--left-column" userId={userId}>
        {children && <div className="chat-room-link--message-snippet">{children}</div>}
      </ChatEtUserCard>
    </PlainButton>
  );
};

ChatUserLink.propTypes = {
  children: PropTypes.any,
  onClick: PropTypes.func,
  userId: PropTypes.any,
  room: PropTypes.object,
};

const ChatRoomLink = ({ room = {}, onClick, children, name, poolId, className }) => {
  const { addUserToRoom, mixPanelEvents } = useChatDependencyContext();
  const [, dispatchNav] = useChatNavigationContext();
  const num_users_in_room = room && getUserTotalFromRoom(room.userIds);
  const [loading, setLoading] = React.useState(false);

  return (
    <PlainButton
      className={`chat-room-link${className ? " " + className : ""}`}
      onClick={() => {
        onClick();
        mixPanelEvents.startPoolChat();
        if (room.id) {
          dispatchNav({ type: "SELECT_ROOM", payload: room.id });
        } else {
          setLoading(true);
          // only an admin in core should be able to add themselves to a room.
          // an app controls the building of the rooms (pools list) so VMS users should not
          // have rooms without ids
          addUserToRoom(poolId)
            .then(r => {
              setLoading(false);
              dispatchNav({ type: "SELECT_ROOM", payload: r.external_room_id });
            })
            .catch(() => {
              setLoading(false);
            });
        }
      }}
    >
      <Flex className="chat-room-link--left-column">
        <Icon icon="person" className="chat-room--users-icon" />
        {Boolean(num_users_in_room) && num_users_in_room}
      </Flex>
      <div className="chat-room-link--sub-section">
        <div className={classNames("chat-room-link--name", { "is-member": room.id })}>{name}</div>
        {loading && <Loading size="small" />}
        {children && <div className="chat-room-link--message-snippet">{children}</div>}
      </div>
    </PlainButton>
  );
};

ChatRoomLink.propTypes = {
  children: PropTypes.any,
  name: PropTypes.string,
  onClick: PropTypes.func,
  room: PropTypes.object,
  poolId: PropTypes.number,
};

const ChatLink = ({ name, onClick = () => {}, room = {}, children, userId, ...props }) => {
  if (userId) return <ChatUserLink onClick={onClick} children={children} userId={userId} room={room} {...props} />;

  return <ChatRoomLink name={name} onClick={onClick} room={room} children={children} {...props} />;
};

ChatLink.propTypes = {
  name: PropTypes.string,
  onClick: PropTypes.func,
  room: PropTypes.object,
  userId: PropTypes.any,
  children: PropTypes.any,
};

export default ChatLink;
